// Ленивая загрузка скрипта Cloudflare Turnstile — только на страницах, где
// форма может потребовать капчу. НЕ грузится из index.html: не тащим
// сторонний скрипт на все страницы лендинга/чата (TZ_turnstile_captcha.md, 4.1).
import { useEffect, useState } from 'react'
import { useAuthConfig } from './useAuthConfig'

const SCRIPT_ID = 'cf-turnstile-script'
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

declare global {
	interface Window {
		turnstile?: {
			render: (container: string | HTMLElement, options: Record<string, unknown>) => string
			remove: (widgetId: string) => void
			reset: (widgetId: string) => void
		}
	}
}

let scriptLoadPromise: Promise<void> | null = null

function loadTurnstileScript(): Promise<void> {
	if (typeof window === 'undefined') return Promise.resolve()
	if (window.turnstile) return Promise.resolve()
	if (scriptLoadPromise) return scriptLoadPromise

	scriptLoadPromise = new Promise((resolve, reject) => {
		const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
		if (existing) {
			existing.addEventListener('load', () => resolve())
			existing.addEventListener('error', () => reject(new Error('turnstile script failed to load')))
			return
		}
		const script = document.createElement('script')
		script.id = SCRIPT_ID
		script.src = SCRIPT_SRC
		script.async = true
		script.defer = true
		script.onload = () => resolve()
		script.onerror = () => reject(new Error('turnstile script failed to load'))
		document.head.appendChild(script)
	})
	return scriptLoadPromise
}

export interface UseTurnstileResult {
	/** Скрипт CF загружен и window.turnstile доступен — можно рендерить виджет. */
	ready: boolean
	sitekey: string | null
	enabled: boolean
	/** null, пока GET /auth/config ещё не ответил. */
	loginMode: 'adaptive' | 'always' | 'off' | null
	alwaysOn: string[]
}

export function useTurnstile(): UseTurnstileResult {
	const config = useAuthConfig()
	const [ready, setReady] = useState(typeof window !== 'undefined' && !!window.turnstile)
	const enabled = !!config?.turnstile.enabled

	useEffect(() => {
		if (!enabled || ready) return
		let cancelled = false
		loadTurnstileScript()
			.then(() => { if (!cancelled) setReady(true) })
			.catch(() => { /* сеть/CF недоступны на клиенте — форма остаётся без виджета, бэк отклонит попытку с понятным текстом */ })
		return () => { cancelled = true }
	}, [enabled, ready])

	return {
		ready: ready && enabled,
		sitekey: config?.turnstile.sitekey ?? null,
		enabled,
		loginMode: config?.turnstile.loginMode ?? null,
		alwaysOn: config?.turnstile.alwaysOn ?? [],
	}
}
